import { createElement, useEffect, useRef, useState } from "react";

/**
 * Fades + slides its content in as it scrolls into view (once).
 * CSS-driven (no animation library): toggles the `is-visible` class
 * via IntersectionObserver. Honors prefers-reduced-motion through the
 * CSS (see .reveal rules in index.css). Designed to replace an existing
 * block element — pass the same tag via `as` plus className/id/style.
 *
 * Props:
 *   as        element/tag to render (default "section")
 *   delay     stagger delay in seconds (default 0)
 *   className extra classes (merged with the reveal classes)
 *   children, id, style, ...rest  passed through
 */
export default function Reveal({ as = "section", delay = 0, className = "", style, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // If IntersectionObserver is unavailable, just show immediately.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "0px 0px -80px 0px", threshold: 0.05 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const classes = ["reveal", visible ? "is-visible" : "", className].filter(Boolean).join(" ");
  const mergedStyle = delay ? { ...style, "--reveal-delay": `${delay}s` } : style;

  return createElement(as, { ...rest, ref, className: classes, style: mergedStyle }, children);
}
